import {
    IconChevronDown,
    IconChevronUp,
    IconHandStop,
    IconMinus,
    IconPlus,
} from '@tabler/icons-react';
import { TOOLS, ZOOM_MAX, ZOOM_MIN } from '../constants';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { usePdfEditorStore } from '../state/usePdfEditorStore';

/**
 * Floating page/zoom pill, mirroring the reader controls of a PDF viewer.
 *
 * The status strip along the foot is the editor's chrome; this is the reader's.
 * It stays within thumb reach of the page itself, and it carries the pan
 * toggle, which the strip has no room for.
 */
export default function FloatingPageBar({ scale }) {
    const { t } = useTranslation();
    const pages = usePdfEditorStore((s) => s.pages);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const goToPage = usePdfEditorStore((s) => s.goToPage);
    const zoomIn = usePdfEditorStore((s) => s.zoomIn);
    const zoomOut = usePdfEditorStore((s) => s.zoomOut);
    const activeTool = usePdfEditorStore((s) => s.activeTool);
    const setTool = usePdfEditorStore((s) => s.setTool);

    const step = (delta) => goToPage(Math.min(pages.length - 1, Math.max(0, currentPage + delta)));

    // Under a fit mode the viewport sizes the page from its own width and the
    // stored zoom is not what you are looking at, so the pill reads the scale
    // the viewport reports back.
    const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale || 1));
    const pct = Math.round(zoom * 100);

    const btn = 'flex h-8 w-8 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 disabled:opacity-35';

    return (
        <div className="pointer-events-none absolute bottom-5 left-1/2 z-20 -translate-x-1/2">
            <div className="pointer-events-auto flex items-center gap-1 rounded-full bg-[#2A2521] px-2.5 py-1.5 shadow-[0_8px_24px_rgba(28,24,21,0.32)]">
                <span className="pl-1.5 pr-1 text-[12.5px] font-medium text-white/70">
                    {t('pdfEditor.page')}
                </span>
                <button
                    type="button"
                    onClick={() => step(-1)}
                    disabled={currentPage <= 0}
                    className={btn}
                    aria-label={t('pdfEditor.prevPage')}
                >
                    <IconChevronUp size={16} stroke={2} />
                </button>
                <span className="min-w-[54px] text-center font-mono text-[12.5px] text-white">
                    {toBnDigits(currentPage + 1)}/{toBnDigits(pages.length)}
                </span>
                <button
                    type="button"
                    onClick={() => step(1)}
                    disabled={currentPage >= pages.length - 1}
                    className={btn}
                    aria-label={t('pdfEditor.nextPage')}
                >
                    <IconChevronDown size={16} stroke={2} />
                </button>

                <span className="mx-1 h-5 w-px bg-white/15" />

                <button
                    type="button"
                    onClick={zoomOut}
                    disabled={zoom <= ZOOM_MIN}
                    className={btn}
                    aria-label={t('pdfEditor.zoomOut')}
                >
                    <IconMinus size={16} stroke={2} />
                </button>
                <span className="min-w-[46px] text-center font-mono text-[12.5px] text-white">
                    {pct}%
                </span>
                <button
                    type="button"
                    onClick={zoomIn}
                    disabled={zoom >= ZOOM_MAX}
                    className={btn}
                    aria-label={t('pdfEditor.zoomIn')}
                >
                    <IconPlus size={16} stroke={2} />
                </button>

                <span className="mx-1 h-5 w-px bg-white/15" />

                <button
                    type="button"
                    onClick={() => setTool(activeTool === TOOLS.HAND ? TOOLS.SELECT : TOOLS.HAND)}
                    className={`${btn} ${activeTool === TOOLS.HAND ? 'bg-white/20' : ''}`}
                    aria-label={t('pdfEditor.tools.pan')}
                    aria-pressed={activeTool === TOOLS.HAND}
                >
                    <IconHandStop size={16} stroke={1.9} />
                </button>
            </div>
        </div>
    );
}
